import App from 'antd/lib/app';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ICreateSavedFilter, ISavedFilter, SavedFilterSubType } from '../../../components/sidebar-filters/saved-filter/interface';
import { useSavedFiltersList } from '../../../components/sidebar-filters/useSavedFiltersList';
import { DEFAULT_PAGINATION } from '../../../domain/commonConst';
import { useSavedFilterCreateMutation, useSavedFilterDeleteMutation, useSavedFilterUpdateMutation } from '../../../domain/saved-filters/queries';
import { useSidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { usePageLocation } from '../../usePageState';
import {
    checkFilterInvalidValues,
    countNotEmptyFilters,
    transformNominationsSavedFilterValuesFromDto,
    transformNominationsSavedFilterValuesToDto,
} from './utils';

export interface INominationsPageState {
    // Filters
    filter?: {
        site_id?: number | number[];
        enrollment_id?: string | string[];
        account_manager?: string | string[];
        iso?: string | string[];
        utility_id?: string | string[];
        lmp_market?: string | string[];
        program_name?: number | number[];
        control_provider_name?: number | number[];
        enrollment_status?: string | string[];
        nomination_recency?: string | string[];
        nomination_status?: string | string[];
    };

    // Used by whole page
    companyId?: number;
    search?: string;

    pagination?: {
        current: number;
        pageSize: number;
    };

    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export interface INominationsSidebarFilters {
    site_id?: number[];
    enrollment_id?: string[];
    account_manager?: string[];
    iso?: string[];
    utility_id?: string[];
    lmp_market?: string[];
    program_name?: number[];
    control_provider_name?: number[];
    enrollment_status?: string[];
    nomination_recency?: string[];
    nomination_status?: string[];
}

export interface INominationsSidebarFiltersContext {
    filtersCount: number;
    filters: INominationsSidebarFilters;
    savedFilters: ISavedFilter[];

    isSavedFiltersLoading: boolean;

    handleFilterChange: (key: string, value: any) => void;
    handleFiltersClear: () => void;

    selectedSavedFilter: ICreateSavedFilter | null;
    isCreateFilterLoading: boolean;
    isUpdateFilterLoading: boolean;
    isDeleteFilterLoading: boolean;

    handleSavedFilterSelect: (filter: ICreateSavedFilter | null) => void;
    handleCreateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleUpdateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleDeleteSavedFilter: (filter: ISavedFilter) => Promise<void>;

    appliedSavedFilter: ISavedFilter | null; // Using for applying filters
    handleApplySavedFilter: (savedFilter: ISavedFilter) => void;
}

export const NominationSidebarFiltersContext = React.createContext<INominationsSidebarFiltersContext>({} as INominationsSidebarFiltersContext);

export const convertNominationsPageStateToFilterState = (pageState: INominationsPageState): INominationsSidebarFilters => {
    const result: INominationsSidebarFilters = {};
    const filterData = pageState.filter || {};

    // Convert site_id
    if (filterData.site_id !== undefined) {
        result.site_id = Array.isArray(filterData.site_id) ? filterData.site_id : [filterData.site_id];
    }

    // Convert enrollment_id
    if (filterData.enrollment_id !== undefined) {
        result.enrollment_id = Array.isArray(filterData.enrollment_id) ? filterData.enrollment_id : [filterData.enrollment_id];
    }

    // Convert account_manager
    if (filterData.account_manager !== undefined) {
        result.account_manager = Array.isArray(filterData.account_manager) ? filterData.account_manager : [filterData.account_manager];
    }

    // Convert iso
    if (filterData.iso !== undefined) {
        result.iso = Array.isArray(filterData.iso) ? filterData.iso : [filterData.iso];
    }

    // Convert utility
    if (filterData.utility_id !== undefined) {
        result.utility_id = Array.isArray(filterData.utility_id) ? filterData.utility_id : [filterData.utility_id];
    }

    // Convert market
    if (filterData.lmp_market !== undefined) {
        result.lmp_market = Array.isArray(filterData.lmp_market) ? filterData.lmp_market : [filterData.lmp_market];
    }

    // Convert program
    if (filterData.program_name !== undefined) {
        result.program_name = Array.isArray(filterData.program_name) ? filterData.program_name : [filterData.program_name];
    }

    // Convert control_provider
    if (filterData.control_provider_name !== undefined) {
        result.control_provider_name = Array.isArray(filterData.control_provider_name)
            ? filterData.control_provider_name
            : [filterData.control_provider_name];
    }

    // Convert enrollment_status
    if (filterData.enrollment_status !== undefined) {
        result.enrollment_status = Array.isArray(filterData.enrollment_status) ? filterData.enrollment_status : [filterData.enrollment_status];
    }

    // Convert nomination_recency
    if (filterData.nomination_recency !== undefined) {
        result.nomination_recency = Array.isArray(filterData.nomination_recency) ? filterData.nomination_recency : [filterData.nomination_recency];
    }

    // Convert nomination_status
    if (filterData.nomination_status !== undefined) {
        result.nomination_status = Array.isArray(filterData.nomination_status) ? filterData.nomination_status : [filterData.nomination_status];
    }

    return result;
};

export const NominationSidebarFiltersProvider = ({ children }: { children: React.ReactNode }) => {
    const { filtersCount, filters, handleFilterChange, handleFiltersChange, handleFiltersClear } = useSidebarFiltering();

    // Saving Filters
    const {
        savedFilters,
        isSavedFiltersLoading,
        selectedSavedFilter,
        appliedSavedFilter,
        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,
        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    } = useNominationsSavedFilters();

    const onFiltersChange = useCallback(
        (key: string, value: unknown) => {
            handleFilterChange(key, value);
            handleApplySavedFilter(null); // Reset applied filter when filters change
        },
        [handleApplySavedFilter, handleFilterChange]
    );

    // Combine logic for applying saved filters
    const onApplySavedFilter = useCallback(
        (savedFilter: ISavedFilter) => {
            const filterValues = savedFilter.filter_values;

            handleFiltersChange(filterValues);
            handleApplySavedFilter(savedFilter);
        },
        [handleApplySavedFilter, handleFiltersChange]
    );

    const onFiltersClear = useCallback(() => {
        handleFiltersClear();
        handleApplySavedFilter(null);
    }, [handleApplySavedFilter, handleFiltersClear]);

    return (
        <NominationSidebarFiltersContext.Provider
            value={{
                filtersCount,
                filters,
                savedFilters,

                isSavedFiltersLoading,

                handleFilterChange: onFiltersChange,
                handleFiltersClear: onFiltersClear,

                selectedSavedFilter,
                isCreateFilterLoading,
                isUpdateFilterLoading,
                isDeleteFilterLoading,
                handleSavedFilterSelect,
                handleCreateSavedFilter,
                handleUpdateSavedFilter,
                handleDeleteSavedFilter,

                appliedSavedFilter,
                handleApplySavedFilter: onApplySavedFilter,
            }}
        >
            {children}
        </NominationSidebarFiltersContext.Provider>
    );
};

const useSidebarFiltering = () => {
    const location = useLocation();
    const { queryToState, setPageQuery } = usePageLocation({ parseNumbers: false });
    const pageState: INominationsPageState = queryToState(location.search);

    const [filters, setFilters] = useState<INominationsSidebarFilters>(convertNominationsPageStateToFilterState(pageState));
    const filtersCount = useMemo(() => countNotEmptyFilters(filters), [filters]);

    const handleFilterChange = (key: string, value: unknown) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
        }));

        // Update the URL query parameters with filter object structure
        const updatedPageState = {
            ...pageState,
            filter: {
                ...pageState.filter,
                [key]: value,
            },
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        };

        setPageQuery(updatedPageState);
    };

    const handleFiltersChange = (newFilters: INominationsSidebarFilters) => {
        setFilters(newFilters);
        setPageQuery({
            ...pageState,
            filter: newFilters,
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        });
    };

    const handleFiltersClear = () => {
        setFilters({});

        setPageQuery({
            ...pageState,
            filter: {},
        });
    };

    return {
        filtersCount,
        filters,

        handleFilterChange,
        handleFiltersChange,
        handleFiltersClear,
    };
};

const useNominationsSavedFilters = () => {
    const { notification } = App.useApp();
    const context = useSidebarFilterOptionsContext();

    // This filter state using to track applied saved filter
    const [appliedSavedFilter, setAppliedSavedFilter] = useState<ISavedFilter | null>(null);

    // This filter state using to create/update saved filter
    const [selectedSavedFilter, setSelectedSavedFilter] = useState<ICreateSavedFilter | null>(null);
    const { savedFilters, isLoading: isSavedFiltersLoading } = useSavedFiltersList(SavedFilterSubType.nominations);

    const { mutateAsync: createSavedFilter, isLoading: isCreateFilterLoading } = useSavedFilterCreateMutation();
    const { mutateAsync: deleteSavedFilter, isLoading: isDeleteFilterLoading } = useSavedFilterDeleteMutation();
    const { mutateAsync: updateSavedFilter, isLoading: isUpdateFilterLoading } = useSavedFilterUpdateMutation();

    const validateSavedFilter = useCallback(
        (filter: ISavedFilter) => {
            // Transform filter values from DTO format to Interface
            filter.filter_values = transformNominationsSavedFilterValuesFromDto(filter.filter_values);

            const outdatedFields = checkFilterInvalidValues(filter.filter_values, context);
            // Check if the filter has not existing values in options
            filter.isOutdated = outdatedFields.length > 0;
            filter.outdatedFields = outdatedFields;

            return filter;
        },
        [context]
    );

    const validatedSavedFilters = useMemo(() => {
        if (savedFilters.length === 0) return [];

        return savedFilters.map((filter: any) => validateSavedFilter(filter));
    }, [savedFilters, validateSavedFilter]);

    const handleSavedFilterSelect = (filter: ICreateSavedFilter | null) => {
        setSelectedSavedFilter(filter);
    };

    const handleApplySavedFilter = (savedFilter: ISavedFilter | null) => {
        setAppliedSavedFilter(savedFilter);
    };

    const handleCreateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformNominationsSavedFilterValuesToDto(filterData.filter_values);

            await createSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot save filter!' });
        }
    };

    const handleUpdateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformNominationsSavedFilterValuesToDto(filterData.filter_values);

            await updateSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot update filter!' });
        }
    };

    const handleDeleteSavedFilter = async (filter: ISavedFilter) => {
        try {
            await deleteSavedFilter(filter.id);

            if (selectedSavedFilter?.id === filter.id) {
                setSelectedSavedFilter(null);
            }

            if (appliedSavedFilter?.id === filter.id) {
                setAppliedSavedFilter(null);
            }
        } catch (err) {
            notification.error({ message: 'Cannot delete filter!' });
        }
    };

    return {
        savedFilters: validatedSavedFilters,
        selectedSavedFilter,
        appliedSavedFilter,
        isSavedFiltersLoading,
        validateSavedFilter,

        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,

        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    };
};

export const useNominationsSidebarFiltersContext = () => {
    const context = React.useContext(NominationSidebarFiltersContext);
    if (!context) {
        throw new Error('useNominationsSidebarFiltersContext must be used within NominationSidebarFiltersContext');
    }

    return context;
};
